import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal, Typography, Form, message } from 'antd'
import { ReviewPrescriptions } from './CompleteConsultationModal/ReviewPrescriptions'
import { resolveTemplate, templateHasUnresolvedVariables } from './CompleteConsultationModal/pharmacyNotesUtils'
import { CompleteButton } from './CompleteConsultationModal.styles'
import { useCaseViewContext } from '../../../context'
import { useNavigation } from '../../../context/NavigationContext'
import { stripHtml } from '../../../utils'

const { Title } = Typography

export const CompleteConsultationModal = ({ 
    visible, 
    onCancel, 
    onComplete,
    prescriptions = [],
    loading = false
}) => {
    const { 
        caseDetails, 
        provider,
        patient,
        readOnlyMode,
    } = useCaseViewContext()
    
    const { navigateToCaseQueue } = useNavigation()
    const [form] = Form.useForm()
    
    const [providerPin, setProviderPin] = useState('')
    const [useTFA, setUseTFA] = useState(false)
    const [providerOTPCode, setProviderOTPCode] = useState('')
    const [prescriptionReadyStates, setPrescriptionReadyStates] = useState({})
    const [chartingNote, setChartingNote] = useState('')
    const [patientMessage, setPatientMessage] = useState('')
    const [includePatientMessage, setIncludePatientMessage] = useState(true)
    const [pharmacyNotesVariables, setPharmacyNotesVariables] = useState({})
    const [pharmacyNotesSubmitAttempted, setPharmacyNotesSubmitAttempted] = useState(false)
        
    const hasControlledSubstance = prescriptions.some(medication => 
        medication.schedule && medication.schedule !== "0"
    )
    
    const hasIncompletePharmacyNotes = useMemo(() =>
        prescriptions.some(medication =>
            templateHasUnresolvedVariables(
                medication.pharmacyNotes,
                pharmacyNotesVariables[medication._id]
            )
        ),
        [prescriptions, pharmacyNotesVariables]
    )
    
    const wasPrescribed = prescriptions.length > 0

    const prescriptionsWithReadyState = useMemo(
        () => prescriptions.map(prescription => ({
            ...prescription,
            readyToSign: prescriptionReadyStates[prescription._id] || false
        })),
        [prescriptions, prescriptionReadyStates]
    )

    const chartingRequirementIsMet = useMemo(() => {
        const isWeightManagementCase = caseDetails?.category?.slug === 'weight-management'
        return !isWeightManagementCase || stripHtml(chartingNote).length >= 3
    }, [caseDetails?.category?.slug, chartingNote])

    useEffect(() => {
        if (!visible) {
            setPharmacyNotesVariables({})
            setPharmacyNotesSubmitAttempted(false)
        }
    }, [visible])

    const readyToComplete = useMemo(() => {
        const providerHasPinSet = provider?.pinSet || false
        const providerIsAuthorizedToRxControlledSubstances = provider?.idVerified || false
        const queuedControlledSubstances = prescriptions.filter(medication => medication.schedule && medication.schedule !== "0")
        const hasQueuedControlledSubstance = !!queuedControlledSubstances.length
        const allControlledSubstancesReadyToSign = !queuedControlledSubstances.find(medication => !prescriptionReadyStates[medication._id])
        const controlledSubstanceRequirementsMet = allControlledSubstancesReadyToSign && (useTFA || providerOTPCode?.length === 6)
        const isEPCSReady = hasQueuedControlledSubstance && providerIsAuthorizedToRxControlledSubstances && providerPin?.length === 4 && controlledSubstanceRequirementsMet
        const isNonEPCSReady = !hasQueuedControlledSubstance && ((providerHasPinSet && providerPin?.length === 4) || !providerHasPinSet)
        const patientMessageRequirementMet = !includePatientMessage || patientMessage?.length > 0

        return (isEPCSReady || isNonEPCSReady) &&
            chartingRequirementIsMet &&
            patientMessageRequirementMet
    }, [
        prescriptions,
        providerPin,
        useTFA,
        providerOTPCode,
        chartingRequirementIsMet,
        provider,
        prescriptionReadyStates,
        patientMessage,
        includePatientMessage,
    ])

    const handleSubmit = async () => {
        if (readOnlyMode) {
            message.warning('This case is in read-only mode and cannot be completed.')
            return
        }
        if (!form) {
            console.error('Form is not available')
            return
        }
        if (hasIncompletePharmacyNotes) {
            setPharmacyNotesSubmitAttempted(true)
            return
        }
        const formValues = form.getFieldsValue(true)

        const sanitizedPrescriptions = prescriptions.map(prescription => {
            const sanitizedPrescription = { ...prescription }
            delete sanitizedPrescription.states
            delete sanitizedPrescription.templateAlternatives
            sanitizedPrescription.readyToSign = prescriptionReadyStates[prescription._id] || false
            sanitizedPrescription.pharmacyNotes = resolveTemplate(
                sanitizedPrescription.pharmacyNotes,
                pharmacyNotesVariables[prescription._id]
            )
            return sanitizedPrescription
        })

        const payload = { 
            ...formValues,
            prescriptions: sanitizedPrescriptions,
            needsReview: provider?.tags?.includes('needs review'),
            pin: providerPin,
            useTFA,
            providerOTPCode,
            ...(chartingNote ? { chartingNote } : {}),
        }
        
        try {
            await onComplete(payload)
            message.success('Consultation completed successfully.')
            navigateToCaseQueue();
        } catch (e) {
            message.error(e)
        }
    }

    const handleVariableChange = useCallback((prescriptionId, variableName, value) => {
        setPharmacyNotesVariables(prev => ({
            ...prev,
            [prescriptionId]: {
                ...(prev[prescriptionId] || {}),
                [variableName]: value
            }
        }))
    }, [])

    const handleCancel = useCallback(() => {
        form.resetFields()
        setProviderPin('')
        setProviderOTPCode('')
        setUseTFA(false)
        setChartingNote('')
        onCancel()
    }, [setProviderPin, setProviderOTPCode, setUseTFA, setChartingNote, form, onCancel])

    const handleReadyToSign = useCallback((_id, readyToSign) => {
        setPrescriptionReadyStates(prev => ({
            ...prev,
            [_id]: readyToSign
        }))
    }, [])

    const handleUseTFA = useCallback((useTFAValue) => {
        setUseTFA(useTFAValue)
        if (!useTFAValue) {
            setProviderOTPCode('')
        }
    }, [])

    const handleChartingNoteChange = useCallback((value) => {
        setChartingNote(value)
    }, [])

    const handleClearPatientMessage = useCallback(() => {
        setPatientMessage('')
        form.resetFields(['patientExplanation'])
    }, [form])

    const handleIncludePatientMessageChange = useCallback((include) => {
        setIncludePatientMessage(include)
        if (!include) handleClearPatientMessage()
    }, [handleClearPatientMessage])

    const completionFooter = [
        <CompleteButton
            key="complete"
            onClick={handleSubmit}
            type="primary"
            loading={loading}
            disabled={!readyToComplete}
        >
            Complete Consultation
        </CompleteButton>,
    ]

    if (!visible) return null

    return (
        <Modal
            title={<Title level={3}>Complete Consultation { wasPrescribed ? 'With' : 'Without'} Prescription{prescriptions?.length > 1 ? 's' : ''}</Title>}
            open={visible}
            onCancel={handleCancel}
            width={900}
            footer={completionFooter}
        >
            <ReviewPrescriptions
                hasControlledSubstance={hasControlledSubstance}
                hasIncompletePharmacyNotes={hasIncompletePharmacyNotes}
                cdsOptions={caseDetails?.category?.CDS || []}
                form={form}
                prescriptions={prescriptionsWithReadyState}
                pharmacyNotesVariables={pharmacyNotesVariables}
                pharmacyNotesSubmitAttempted={pharmacyNotesSubmitAttempted}
                onVariableChange={handleVariableChange}
                onReadyToSign={handleReadyToSign}
                onSetProviderPin={setProviderPin}
                onSetOTPCode={setProviderOTPCode}
                onUseTFA={handleUseTFA}
                providerPin={providerPin}
                useTFA={useTFA}
                tfaCode={providerOTPCode}
                provider={provider}
                patient={patient}
                chartingRequirementIsMet={chartingRequirementIsMet}
                onChartingNoteChange={handleChartingNoteChange}
                patientMessage={patientMessage}
                onPatientMessageChange={setPatientMessage}
                onClearPatientMessage={handleClearPatientMessage}
                includePatientMessage={includePatientMessage}
                onIncludePatientMessageChange={handleIncludePatientMessageChange}
            />
        </Modal>
    )
}
